import { useTranslation } from "react-i18next";
import { Spinner, cx } from "./ui";
import type { DatabaseState } from "../lib/types";

const STATE_STYLES: Record<DatabaseState, { styles: string; dot: string }> = {
  creating: { styles: "border-warn/25 bg-warn/8 text-warn", dot: "bg-warn" },
  running: { styles: "border-mint/25 bg-mint/8 text-mint", dot: "bg-mint" },
  stopped: { styles: "border-line bg-raised text-fg-muted", dot: "bg-fg-subtle" },
  error: { styles: "border-danger/25 bg-danger/8 text-danger", dot: "bg-danger" },
};

/**
 * `stale` means the deployment engine could not be reached and this is our own
 * record, which is dimmed and labelled rather than shown as though confirmed.
 *
 * Lives here rather than in `DatabasesPage` because the list and the detail
 * header both draw it, and a badge that means "confirmed" in one place and
 * "last known" in another would be worse than no badge.
 */
export function DatabaseBadge({ state, stale = false }: { state: DatabaseState; stale?: boolean }) {
  const { t } = useTranslation("status");
  const { styles, dot } = STATE_STYLES[state];
  const creating = state === "creating" && !stale;

  return (
    <span
      title={stale ? t("staleTitle") : undefined}
      className={cx(
        "inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[11px] font-medium",
        styles,
        stale && "opacity-60",
      )}
    >
      {creating ? <Spinner className="size-2.5" /> : <span aria-hidden="true" className={cx("size-1.5 rounded-full", dot)} />}
      {t(`database.${state}`)}
      {stale && <span className="text-[0.9em] opacity-80">{t("stale")}</span>}
    </span>
  );
}
